// src/pages/SmartyWasteAnalysisChart.jsx
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import useFetch from '../../hooks/useFetch';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../redux/actions/authActions';

ChartJS.register(ArcElement, Tooltip, Legend);

const SmartyWasteAnalysisChart = () => {
  const authState = useSelector((state) => state.authReducer);
  const { isLoggedIn, user, token } = authState;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [reasonsData, setReasonsData] = useState([]); // For potential list view below chart

  useEffect(() => {
    if (!isLoggedIn) return;

    const handleLogout = () => {
      dispatch(logout());
      navigate('/');
    };

    // Fetch wasted items
    const config = {
      url: '/smartyitems/mysmartyitems',
      method: 'post',
      headers: { Authorization: token },
      data: { 
        userId: user._id, 
        status: 'Wasted' // Or 'Expired' if that's the waste status
      }
    };

    fetchData(config, { showSuccessToast: false }).then((response) => {
      const items = response.smartyItems || [];
      setReasonsData(items); // Store for list if needed

      // Aggregate waste reasons
      const reasonMap = {};
      items.forEach((item) => {
        const reason = item.wasteReason || 'Unknown';
        reasonMap[reason] = (reasonMap[reason] || 0) + 1; // Count occurrences
      });

      const labels = Object.keys(reasonMap);
      const data = Object.values(reasonMap);

      setChartData({
        labels,
        datasets: [
          {
            label: 'Waste Reasons Distribution',
            data,
            backgroundColor: [
              'rgba(255, 99, 132, 0.6)',
              'rgba(54, 162, 235, 0.6)',
              'rgba(255, 205, 86, 0.6)',
              'rgba(75, 192, 192, 0.6)',
              'rgba(153, 102, 255, 0.6)',
              'rgba(255, 159, 64, 0.6)',
              // Add more colors if needed
            ],
            borderColor: [
              'rgba(255, 99, 132, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 205, 86, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 159, 64, 1)',
            ],
            borderWidth: 1,
          },
        ],
      });
    });
  }, [fetchData, isLoggedIn, token, user, dispatch, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-blue-100">
        <div>Loading Waste Analysis...</div>
      </div>
    );
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Waste Reasons Analysis',
        color: '#fbbf24', // Yellow for theme
      },
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-blue-100 p-4">
      {/* Header */}
      <header className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-yellow-200">Waste Reasons Analysis</h1>
        <button
          onClick={() => dispatch(logout())}
          className="p-2 rounded-md bg-blue-600 hover:bg-blue-700 transition"
          title="Logout"
        >
          ⏻
        </button>
      </header>

      {/* Chart */}
      <div className="w-full max-w-2xl mx-auto mb-8">
        <Pie data={chartData} options={options} />
      </div>

      {/* Optional: List of Wasted Items (for details) */}
      {reasonsData.length > 0 && (
        <div className="bg-gray-900 p-4 rounded-lg">
          <h2 className="text-lg font-semibold text-yellow-200 mb-2">Wasted Items Details</h2>
          <ul className="space-y-2 text-sm">
            {reasonsData.map((item) => (
              <li key={item._id} className="border-b border-gray-700 pb-2">
                <span className="font-medium">{item.item}</span> - Reason: {item.wasteReason || 'N/A'} (₹{item.wasteAmount || 0})
              </li>
            ))}
          </ul>
        </div>
      )}

      {reasonsData.length === 0 && (
        <div className="text-center text-yellow-300 mt-8">No wasted items found.</div>
      )}
    </div>
  );
};

export default SmartyWasteAnalysisChart;